<template>
  <div class="h-[calc(100vh-178px)] flex content-stretch gap-x-4" v-if="loaded">
    <ContentBlock class="basis-60" title="Filters">
      <div class="p-4 flex flex-col gap-y-4">
        <SingleObjectSelect :label="isRoadLink ? 'Select Link Road' : 'Select Link Zone'" v-model="filter.selectLink" :options="selectLinks"
          :placeholder="isRoadLink ? 'Select Link Road' : 'Select Link Zone'" inline />
        <SingleObjectSelect label="Month" v-model="filter.month" :options="months" placeholder="Select Month" inline />
        <MultipleSelect label="Trip Purposes" v-model="filter.tripPurposes" :options="tripPurposes" inline v-if="tripPurposes.length > 1" />
        <MultipleSelect label="Day Aggregation" v-model="filter.dayAgg" :options="dayAggs" inline v-if="dayAggs.length > 1" />
        <MultipleSelect label="Hour Aggregation" v-model="filter.hourAgg" :options="hourAggs" :option-label="formatHourAgg" inline v-if="hourAggs.length > 1" />
        <div
          class="mt-8 flex text-zinc-50 space-x-2 items-center justify-center px-3 py-2 rounded-lg border border-accent bg-accent/75 hover:bg-accent text-sm cursor-pointer"
          @click="applyFilter">
          <span>Apply</span>
        </div>
      </div>
    </ContentBlock>
    <div class="relative grow">
      <div class="h-full flex rounded-lg overflow-hidden">
        <div class="bg-zinc-300 dark:bg-zinc-800 w-12 relative">
          <div class="-rotate-90 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 font-semibold tracking-wide whitespace-nowrap">Trip Origins</div>
        </div>
        <div class="grow relative" ref="mapsCompare">
          <div class="absolute inset-0" ref="map0"></div>
          <div class="absolute inset-0 " ref="map1"></div>
          <div class="select-none absolute z-10 bottom-4 right-4 w-28 bg-zinc-100 dark:bg-zinc-800 rounded-md shadow">
            <div class="rounded-t-md bg-zinc-200 dark:bg-zinc-700 p-2 flex justify-between items-center">
              <h5 class="font-medium text-sm">Legend</h5>
            </div>
            <div class="p-2">
              <ul>
                <li class="flex items-center justify-between" v-for="(threshold, color) in legendColors" :key="color">
                  <div class="h-3.5 w-3.5 rounded-full" :style="`background-color: ${color}`"></div>
                  <div>{{ formatNumber(threshold) }}+</div>
                </li>
              </ul>
            </div>
          </div>
        </div>
        <div class="bg-zinc-300 dark:bg-zinc-800 w-12 relative">
          <div class="rotate-90 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 font-semibold tracking-wide whitespace-nowrap">Trip Destinations</div>
        </div>
      </div>
      <div
        class="bg-zinc-100 dark:bg-zinc-800 px-4 py-2 border border-zinc-200 dark:border-zinc-700 rounded-lg shadow-md absolute z-10 left-1/2 top-12 -translate-x-1/2 min-w-64 grid grid-cols-2"
        v-show="showFocusedZoneDetails">
        <div class="col-span-2 text-center text-lg font-semibold mb-1">{{ get(focusedZone,
          `properties.${config.zonePropertyMap.name || config.zonePropertyMap.id}`, "") }}</div>
        <template v-if="focusedZoneOutboundTrips > 0">
          <div>Origin:</div>
          <div class="text-right">{{ formatNumber(focusedZoneOutboundTrips) }}</div>
        </template>
        <template v-if="focusedZoneInboundTrips > 0">
          <div>Destination:</div>
          <div class="text-right">{{ formatNumber(focusedZoneInboundTrips) }}</div>
        </template>
      </div>
    </div>
  </div>
  <div class="h-[calc(100vh-178px)] flex place-items-center justify-center" v-else>
    <IconSpinner class="animate-spin w-12 h-12" />
  </div>
</template>

<script setup>
import { ref, computed, watch, onBeforeUnmount, onMounted, nextTick } from "vue";
import { useRoute } from "vue-router";
import { storeToRefs } from "pinia";
import { useAuth0 } from "@auth0/auth0-vue";
import { first, get, without, uniq } from "lodash";
import { featureCollection } from "@turf/turf";
import { format } from "date-fns";
import { scaleThreshold } from "d3-scale";
import { schemeBlues } from "d3-scale-chromatic";
import { min } from "simple-statistics";
import AirSageMap from "@/lib/map";
import CompareMaps from "@/lib/compareMaps";
import { unfurlNestedDates, weekdayBucketLabels } from "@/lib/utils";
import { formatHourAgg } from "@/lib/tripMatrix/cv";
import ContentBlock from "@/components/ContentBlock.vue";
import SingleObjectSelect from "@/components/SingleObjectSelect.vue";
import MultipleSelect from "@/components/MultipleSelect.vue";
import IconSpinner from "@/components/icons/IconSpinner.vue";
import { StudiesService } from "@/services";
import { useStudyStore } from "@/stores/study";
import { useAlertStore } from "@/stores/alert";

const studyStore = useStudyStore();
const alertStore = useAlertStore();
const route = useRoute();
const { getAccessTokenSilently } = useAuth0();
const { config, zones, roads } = storeToRefs(studyStore);

const loaded = ref(false);
const dataMap = ref({
  matrix: [],
  purpose: [],
  dayAgg: [],
  hourAgg: []
});
let thresholds = [];
let expression = [];
const legendColors = ref([]);

// select links are either study zones (default) or road segments from the study's roads file
const isRoadLink = computed(() => config.value.selectLinkType === "road");
const linkPropertyMap = computed(() => (isRoadLink.value ? config.value.roadPropertyMap : config.value.zonePropertyMap) || {});
const linkFeatures = computed(() => {
  const collection = isRoadLink.value ? roads.value : zones.value;
  return (collection?.features || []).filter((f) => (config.value.selectLinks || []).includes(f.properties[linkPropertyMap.value.id]));
});
const selectedLinkFeature = computed(() => linkFeatures.value.find((f) => f.properties[linkPropertyMap.value.id] === filter.value.selectLink));

onBeforeUnmount(studyStore.reset);

function formatNumber(x) {
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

const readMaterializedView = async (view) => {
  try {
    const { id } = route.params;
    const token = await getAccessTokenSilently();
    return await StudiesService.getMaterializedView(id, {
      headers: {
        Authorization: `Bearer ${token}`
      },
      params: view
    });
  } catch (error) {
    alertStore.alertError(error);
  }
};

const loadZones = async () => {
  try {
    const { id } = route.params;
    const token = await getAccessTokenSilently();
    zones.value = await StudiesService.getZones(id, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
  } catch (error) {
    alertStore.alertError(error);
  }
};

const loadRoads = async () => {
  try {
    const { id } = route.params;
    const token = await getAccessTokenSilently();
    roads.value = await StudiesService.getRoads(id, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
  } catch (error) {
    alertStore.alertError(error);
  }
};

const months = ref([]);
const selectLinks = ref([]);
const tripPurposes = ref([]);
const dayAggs = ref([]);
const hourAggs = ref([]);

onMounted(async () => {
  await loadZones();
  if (isRoadLink.value) {
    await loadRoads();
  }
  months.value = unfurlNestedDates(config.value.dates || []).map((m) => ({ value: format(new Date(m), "yyyyMM"), name: format(new Date(m), "MMM yyyy") }));
  const { id: idKey, name: nameKey } = linkPropertyMap.value;
  selectLinks.value = (config.value.selectLinks || []).map((sl) => ({
    name: String(get(linkFeatures.value.find((f) => f.properties[idKey] === sl), `properties.${nameKey || idKey}`, sl)),
    value: sl
  }));
  tripPurposes.value = uniq(Object.values(config.value.tripPurposeBuckets));
  dayAggs.value = weekdayBucketLabels(config.value.weekdayBuckets);
  hourAggs.value = without(uniq(Object.values(config.value.hourBuckets)), "Omit", "omit");
  loaded.value = true;
  await nextTick();
  filter.value.month = first(months.value).value;
  filter.value.selectLink = first(selectLinks.value).value;
  filter.value.tripPurposes = tripPurposes.value;
  filter.value.dayAgg = dayAggs.value;
  filter.value.hourAgg = hourAggs.value;
  loadMaps();
});

const filter = ref({
  month: "",
  selectLink: "",
  tripPurposes: [],
  dayAgg: [],
  hourAgg: []
});

async function applyFilter() {
  try {
    const proxyFilter = {
      month: filter.value.month,
      selectLink: filter.value.selectLink,
      tripPurposes: filter.value.tripPurposes,
      dayAgg: filter.value.dayAgg,
      hourAgg: filter.value.hourAgg
    };
    const data = await Promise.all([
      readMaterializedView({ name: "getThresholds", ...proxyFilter }),
      readMaterializedView({ name: "getMatrix", ...proxyFilter }),
      readMaterializedView({ name: "getTripsByPurpose", ...proxyFilter }),
      readMaterializedView({ name: "getTripsByDayAgg", ...proxyFilter }),
      readMaterializedView({ name: "getTripsByHourAgg", ...proxyFilter })
    ]);
    thresholds = data.shift();
    dataMap.value.matrix = data.shift();
    dataMap.value.purpose = data.shift();
    dataMap.value.dayAgg = data.shift();
    dataMap.value.hourAgg = data.shift();
    loadLayers();
  } catch (error) {
    alertStore.alertError(error);
  }
}

const OUTBOUND_INDEX = 0;
const INBOUND_INDEX = 1;
const mapsReady = ref({
  outbound: false,
  inbound: false
});
const maps = [];

const ZONE_SHAPE_LAYER_ID = "zoneShapes";
const ZONE_SOURCE_ID = "zones";
const SELECT_LINK_SOURCE_ID = "selectLinkZones";
const SELECT_LINK_ZONE_SHAPE_ID = "selectLinkZoneShape";
const SELECT_LINK_ZONE_LINE_ID = "selectLinkZoneLine";
const SELECT_LINK_ROAD_CASING_ID = "selectLinkRoadCasing";
const SELECT_LINK_ROAD_LINE_ID = "selectLinkRoadLine";
const SELECT_LINK_ROAD_HIT_ID = "selectLinkRoadHit";
const SELECT_LINK_COLOR = "#F86624";
const roadEventsBound = new WeakSet();


let hoverStateId;
const focusedZone = ref(null);
const focusedZoneOutboundTrips = ref(0);
const focusedZoneInboundTrips = ref(0);
const showFocusedZoneDetails = ref(false);

const generateExpression = (thresholds, tileKey) => {
  const arrayToObject = (array) => {
    const legend = {};
    array.unshift(0);
    while (array.length) {
      const color = array.shift();
      const threshold = array.shift();
      legend[threshold] = color;
    }
    return legend;
  };
  const numCategories = min([thresholds.length, 8]);
  const colors = schemeBlues[numCategories + 1];
  const scale = scaleThreshold(thresholds, colors);
  let expression = [];
  expression.push(scale(0))
  scale.domain().forEach((d) => {
    expression.push(d, scale(d));
  });
  expression = ["step", ["to-number", ["get", tileKey]]].concat(expression);
  legendColors.value = arrayToObject(expression.slice(2));
  return expression;
};

const loadLayers = () => {
  expression = generateExpression(thresholds, "total");
  loadLayer(maps[OUTBOUND_INDEX], "outbound");
  loadLayer(maps[INBOUND_INDEX], "inbound");
}

const loadLayer = async (map, direction) => {
  const { id } = route.params;
  if (map) {
    for (const layer of [ZONE_SHAPE_LAYER_ID, SELECT_LINK_ZONE_SHAPE_ID, SELECT_LINK_ZONE_LINE_ID, SELECT_LINK_ROAD_CASING_ID, SELECT_LINK_ROAD_LINE_ID, SELECT_LINK_ROAD_HIT_ID]) {
      if (map.getLayer(layer)) {
        map.removeLayer(layer);
      }
    }

    if (map.getSource(ZONE_SOURCE_ID)) {
      map.removeSource(ZONE_SOURCE_ID);
    }

    if (!map.getSource(SELECT_LINK_SOURCE_ID)) {
      map.addSource(SELECT_LINK_SOURCE_ID, {
        type: "geojson",
        data: featureCollection(linkFeatures.value),
        promoteId: linkPropertyMap.value.id
      });
    }
    map.addSource(ZONE_SOURCE_ID, {
      type: "vector",
      tiles: [
        `${import.meta.env.VITE_MARTIN_URL}/get_select_link_${direction}_trips/{z}/{x}/{y}?${new URLSearchParams([
          ...Object.entries({
            id,
            month: filter.value.month,
            select_link: filter.value.selectLink,
            trip_purposes: `{${filter.value.tripPurposes}}`,
            local_day_aggs: `{${filter.value.dayAgg}}`,
            local_hour_aggs: `{${filter.value.hourAgg}}`
          })
        ]).toString()}`
      ],
      promoteId: "zone_id",
      minzoom: 0,
      maxzoom: 8
    });

    map.addLayer({
      id: ZONE_SHAPE_LAYER_ID,
      type: "fill",
      source: ZONE_SOURCE_ID,
      "source-layer": `${direction}-trip-matrix`,
      paint: {
        "fill-color": expression,
        "fill-opacity": ["case", ["boolean", ["feature-state", "hover"], false], 0.92, 0.67]
      }
    });

    map.on("mousemove", ZONE_SHAPE_LAYER_ID, (e) => {
      const [feature] = e.features;
      if (feature) {
        const { id } = feature;
        if (id) {
          map.getCanvas().style.cursor = "pointer";
          if (hoverStateId) {
            maps[INBOUND_INDEX].setFeatureState({
              source: ZONE_SOURCE_ID,
              sourceLayer: "inbound-trip-matrix",
              id: hoverStateId
            }, { hover: false });
            maps[OUTBOUND_INDEX].setFeatureState({
              source: ZONE_SOURCE_ID,
              sourceLayer: "outbound-trip-matrix",
              id: hoverStateId
            }, { hover: false });
          }
          hoverStateId = id;
          maps[INBOUND_INDEX].setFeatureState({
            source: ZONE_SOURCE_ID,
            sourceLayer: "inbound-trip-matrix",
            id: hoverStateId
          }, { hover: true });
          maps[OUTBOUND_INDEX].setFeatureState({
            source: ZONE_SOURCE_ID,
            sourceLayer: "outbound-trip-matrix",
            id: hoverStateId
          }, { hover: true });

          focusedZone.value = zones.value.features.find((z) => z.properties[config.value.zonePropertyMap.id] === hoverStateId);
          focusedZoneOutboundTrips.value = dataMap.value.matrix.filter((m) => m.origin_id === id).reduce((total, m) => total + m.total, 0);
          focusedZoneInboundTrips.value = dataMap.value.matrix.filter((m) => m.destination_id === id).reduce((total, m) => total + m.total, 0);
          showFocusedZoneDetails.value = focusedZoneOutboundTrips.value || focusedZoneInboundTrips.value;
        }
      }
    });

    map.on("mouseleave", ZONE_SHAPE_LAYER_ID, () => {
      map.getCanvas().style.cursor = "";
      if (hoverStateId) {
        maps[INBOUND_INDEX].setFeatureState({
          source: ZONE_SOURCE_ID,
          sourceLayer: "inbound-trip-matrix",
          id: hoverStateId
        }, { hover: false });
        maps[OUTBOUND_INDEX].setFeatureState({
          source: ZONE_SOURCE_ID,
          sourceLayer: "outbound-trip-matrix",
          id: hoverStateId
        }, { hover: false });
      }
      hoverStateId = null;

      focusedZone.value = null;
      focusedZoneOutboundTrips.value = 0;
      focusedZoneInboundTrips.value = 0;
      showFocusedZoneDetails.value = false;
    });

    if (isRoadLink.value) {
      addRoadLayers(map);
    } else {
      map.addLayer({
        id: SELECT_LINK_ZONE_SHAPE_ID,
        type: "fill",
        source: SELECT_LINK_SOURCE_ID,
        paint: {
          "fill-color": SELECT_LINK_COLOR,
          "fill-opacity": ["case", ["boolean", ["feature-state", "hover"], false], 0.92, 0.67]
        }
      });

      map.addLayer({
        id: SELECT_LINK_ZONE_LINE_ID,
        type: "line",
        source: SELECT_LINK_SOURCE_ID,
        paint: {
          "line-color": SELECT_LINK_COLOR,
          "line-width": 1,
          "line-opacity": ["case", ["boolean", ["feature-state", "hover"], false], 0.75, 0.25]
        }
      });

      map.setFilter(SELECT_LINK_ZONE_SHAPE_ID, ["==", linkPropertyMap.value.id, filter.value.selectLink]);
      map.setFilter(SELECT_LINK_ZONE_LINE_ID, ["==", linkPropertyMap.value.id, filter.value.selectLink]);
    }

    if (selectedLinkFeature.value) {
      map.centerOn(selectedLinkFeature.value, { easing: () => 1, padding: 250 });
    }
  }
};

/**
 * Road segment select links: every road is drawn faintly so the user can see
 * (and click) the alternatives, the selected road is drawn bold with a casing.
 */
const addRoadLayers = (map) => {
  const isSelected = ["==", ["get", linkPropertyMap.value.id], filter.value.selectLink];
  map.addLayer({
    id: SELECT_LINK_ROAD_CASING_ID,
    type: "line",
    source: SELECT_LINK_SOURCE_ID,
    layout: { "line-cap": "round", "line-join": "round" },
    paint: {
      "line-color": "#1c1917",
      "line-width": ["case", isSelected, 9, 0],
      "line-opacity": 0.6
    }
  });
  map.addLayer({
    id: SELECT_LINK_ROAD_LINE_ID,
    type: "line",
    source: SELECT_LINK_SOURCE_ID,
    layout: { "line-cap": "round", "line-join": "round" },
    paint: {
      "line-color": SELECT_LINK_COLOR,
      "line-width": ["case", isSelected, 5, ["boolean", ["feature-state", "hover"], false], 4, 2.5],
      "line-opacity": ["case", isSelected, 1, ["boolean", ["feature-state", "hover"], false], 0.9, 0.45]
    }
  });
  // wide invisible line to make the thin roads easy to hover/click
  map.addLayer({
    id: SELECT_LINK_ROAD_HIT_ID,
    type: "line",
    source: SELECT_LINK_SOURCE_ID,
    paint: { "line-width": 16, "line-opacity": 0 }
  });

  if (!roadEventsBound.has(map)) {
    roadEventsBound.add(map);
    let hoveredRoad = null;
    map.on("mousemove", SELECT_LINK_ROAD_HIT_ID, (e) => {
      const [feature] = e.features;
      if (!feature || feature.id === hoveredRoad) {
        return;
      }
      if (hoveredRoad !== null) {
        map.setFeatureState({ source: SELECT_LINK_SOURCE_ID, id: hoveredRoad }, { hover: false });
      }
      hoveredRoad = feature.id;
      map.setFeatureState({ source: SELECT_LINK_SOURCE_ID, id: hoveredRoad }, { hover: true });
      map.getCanvas().style.cursor = "pointer";
    });
    map.on("mouseleave", SELECT_LINK_ROAD_HIT_ID, () => {
      if (hoveredRoad !== null) {
        map.setFeatureState({ source: SELECT_LINK_SOURCE_ID, id: hoveredRoad }, { hover: false });
      }
      hoveredRoad = null;
      map.getCanvas().style.cursor = "";
    });
    map.on("click", SELECT_LINK_ROAD_HIT_ID, (e) => {
      const [feature] = e.features;
      if (feature && feature.id !== filter.value.selectLink) {
        filter.value.selectLink = String(feature.id);
        applyFilter();
      }
    });
  }
};

watch(mapsReady.value, () => {
  if (mapsReady.value.inbound && mapsReady.value.outbound) {
    new CompareMaps(...maps, mapsCompare.value);
    applyFilter();
  }
}, {
  deep: true
});

const map0 = ref(null);
const map1 = ref(null);
const mapsCompare = ref(null);
const loadMaps = () => {
  maps.push(new AirSageMap(map0.value, {
    eventHandlers: {
      "style.load": () => {
        mapsReady.value.outbound = true;
      }
    }
  }));
  maps.push(new AirSageMap(map1.value, {
    eventHandlers: {
      "style.load": () => {
        mapsReady.value.inbound = true;
      }
    }
  }));
};
</script>

<style>
@import "@/assets/style/compare.css";
</style>
